import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import useEventListener from "hooks/useEventListener";
import useEventCallback from "hooks/useEventCallback";

declare global {
  interface WindowEventMap {
    "local-storage": CustomEvent;
  }
}

type UseLocalStorageReturn<T> = [T, Dispatch<SetStateAction<T>>, () => void];

/**
 * useLocalStorage is a custom React hook that synchronizes a state variable with local storage.
 * It provides a way to persist state across page reloads and browser sessions.
 *
 * The hook returns the current value, a function to update the value, and a function to remove the value from local storage.
 * @see https://usehooks-ts.com/react-hook/use-local-storage
 *
 * @example
 * const [name, setName, removeName] = useLocalStorage<string>("name", "John Doe");
 */
function useLocalStorage<T>(key: string, initialValue: T): UseLocalStorageReturn<T> {
  const serializer = useCallback<(value: T) => string>((value) => JSON.stringify(value), []);

  const deserializer = useCallback<(value: string) => T>(
    (value) => {
      if (value === "undefined") {
        return undefined as unknown as T;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(value);
      } catch (error) {
        console.error("Error parsing JSON:", error);
        return initialValue; // Return initialValue if parsing fails
      }

      return parsed as T;
    },
    [initialValue]
  );

  // Get from local storage then parse stored json or return initialValue
  const readValue = useCallback((): T => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? deserializer(raw) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key, deserializer]);

  const [storedValue, setStoredValue] = useState(() => initialValue);

  // Return a wrapped version of useState's setter function that persists the new value to localStorage.
  const setValue: Dispatch<SetStateAction<T>> = useEventCallback((value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(readValue()) : value;
      // Save to local storage
      window.localStorage.setItem(key, serializer(newValue));
      // Save state
      setStoredValue(newValue);
      // We dispatch a custom event so every similar useLocalStorage hook is notified
      window.dispatchEvent(new StorageEvent("local-storage", { key }));
    } catch (error) {
      console.warn(`Error setting localStorage key “${key}”:`, error);
    }
  });

  const removeValue = useEventCallback(() => {
    // Remove the key from local storage
    window.localStorage.removeItem(key);
    // Save state with default value
    setStoredValue(initialValue);
    // We dispatch a custom event so every similar useLocalStorage hook is notified
    window.dispatchEvent(new StorageEvent("local-storage", { key }));
  });

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if ((event as StorageEvent).key && (event as StorageEvent).key !== key) {
        // If the event is from another window and the key is different, ignore it
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue]
  );

  // The storage event of the Window interface fires when another document that shares the same storage area
  // (either localStorage or sessionStorage) as the current window updates that storage area.
  // The event is not fired on the window that made the change.
  useEventListener("storage", handleStorageChange);

  // this is a custom event, triggered in writeValueToLocalStorage
  useEventListener("local-storage", handleStorageChange);

  return [storedValue, setValue, removeValue];
}

export default useLocalStorage;
